'use client';

import { uniqBy } from 'es-toolkit';
import { t } from 'mobx-state-tree';

import { PropertyType } from '../propertyType';

import { PropertyBase } from './propertyBase';

import type { SelectOptionsTypes } from '../types';
import type { Instance, SnapshotIn } from 'mobx-state-tree';

import { displayCase } from '@/editor/core/nameHelpers';


export const MultiSelectPropertyBuilder = {
  create: (params: SnapshotIn<typeof MultiSelectProperty> & { selectOptions?: SelectOptionsTypes }) => {
    const property = MultiSelectProperty.create(params);
    if (params.selectOptions) { property.setOptions(params.selectOptions); }
    return property;
  }
};

/**
 * Model for multi-select property
 * NOTE: Use MultiSelectPropertyBuilder.create() to create a multi-select property
 */
export const MultiSelectProperty = t.compose('MultiSelectProperty', PropertyBase,
  t.model({
    type: t.literal(PropertyType.MultiSelect),
    value: t.array(t.string),
    defaultValue: t.array(t.string),
    options: t.array(t.model({
      label: t.string,
      value: t.string,
    })),
    placeholder: t.optional(t.string, ''),
    maxSelected: t.optional(t.number, Number.MAX_SAFE_INTEGER),
    creatable: t.optional(t.boolean, false),
    useDisplayCase: t.optional(t.boolean, true),
  })
).actions(self => ({
  setValue(values: string[], fromPanel = true, initial = false) {
    // Only validate values exist in options if not creatable
    if (!self.creatable) {
      const invalidValues = values.filter(value => !self.options.some(opt => opt.value === value));
      if (invalidValues.length > 0) {
        console.warn(`Invalid option values: ${invalidValues.join(', ')}`);
        return;
      }
    }

    // Check if values are the same
    if (JSON.stringify([...values].sort()) === JSON.stringify([...self.value].sort())) return;

    self.value.replace(values);
    self.notifyValueChange(self.path, [...values], { fromPanel, initial, property: self as Instance<typeof MultiSelectProperty> });
  },
  setDefaultValue(values: string[]) {
    // Validate all values exist in options
    const invalidValues = values.filter(value => !self.options.some(opt => opt.value === value));
    if (invalidValues.length > 0) {
      console.warn(`Invalid default option values: ${invalidValues.join(', ')}`);
      return;
    }
    self.defaultValue.replace(values);
  },
  addValue(value: string) {
    // Only validate value exists in options if not creatable
    if (!self.creatable && !self.options.some(opt => opt.value === value)) {
      console.warn(`Invalid option value: ${value}`);
      return;
    }
    if (self.value.includes(value)) return;
    if (self.value.length >= self.maxSelected) return;

    const newValues = [...self.value, value];
    self.value.replace(newValues);
    self.notifyValueChange(self.path, newValues, { fromPanel: true, initial: false, property: self as Instance<typeof MultiSelectProperty> });
  },
  removeValue(value: string) {
    if (!self.value.includes(value)) return;
    const newValues = self.value.filter(v => v !== value);
    self.value.replace(newValues);
    self.notifyValueChange(self.path, newValues, { fromPanel: true, initial: false, property: self as Instance<typeof MultiSelectProperty> });
  },
  clearValues() {
    self.value.clear();
    self.notifyValueChange(self.path, [], { fromPanel: true, initial: false, property: self as Instance<typeof MultiSelectProperty> });
  },
  setOptions(options: SelectOptionsTypes) {
    let mappedOptions;
    if (Array.isArray(options)) {
      if (options.length && typeof options[0] === 'string') {
        mappedOptions = (options as string[]).map(opt => ({ label: self.useDisplayCase ? displayCase(opt) : opt, value: opt }));
      } else {
        mappedOptions = (options as Array<{ label: string; value: string }>)
          .map(opt => ({ label: self.useDisplayCase ? displayCase(opt.label) : opt.label, value: opt.value }));
      }
    } else {
      mappedOptions = Object.entries(options)
        .map(([value, label]) => ({ label: self.useDisplayCase ? displayCase(label) : label, value }));
    }
    self.options.replace(uniqBy(mappedOptions, opt => opt.label));
  },
  addOption(option: { label: string, value: string }) {
    if (!self.options.some(opt => opt.label === option.label)) {
      self.options.push(option);
    }
  },
  removeOption(value: string) {
    const index = self.options.findIndex(opt => opt.value === value);
    if (index !== -1) self.options.splice(index, 1);
  },
  setMaxSelected(max: number) {
    self.maxSelected = max;
    // If current selection exceeds new max, trim it
    if (self.value.length > max) {
      const trimmedValues = self.value.slice(0, max);
      self.value.replace(trimmedValues);
      self.notifyValueChange(self.path, trimmedValues, { fromPanel: true, initial: false, property: self as Instance<typeof MultiSelectProperty> });
    }
  },
  setCreatable(creatable: boolean) {
    self.creatable = creatable;
  }
}));
